import { useMemo, useState } from 'react'
import { shift } from '../lib/spanStart'

/** A span of days, and the two fields the editor moves it with. */
export interface SeededSpan {
  /** The first day of the span, `YYYY-MM-DD`. */
  date: string
  /** The last day, as the editor or the template's default span left it. */
  toDate: string
  /** The last day the span covers - the same day as `toDate`. */
  lastDate: string
  /**
   * Whether the range runs forwards. A backwards one is not worth asking about,
   * and the two setters below see to it that there is none: neither end can be
   * left behind the other. This is the backstop for whoever draws on the range
   * without going through them.
   */
  validRange: boolean
  /**
   * Take the editor's first day, which is what stops it being seeded. An empty
   * one is a cleared field rather than a day, and starts the seeding again.
   *
   * A first day past the last one takes the last one with it, the span having
   * no way of running backwards.
   */
  setDate: (value: string) => void
  /**
   * Take the editor's last day, the same way, cleared field and all - except
   * that a day behind the first one is the first one, the span being the other
   * end's to move.
   */
  setToDate: (value: string) => void
  /**
   * Move the whole span that many days. A last day the editor set travels with
   * the first, keeping the span the length they gave it.
   */
  shiftBy: (days: number) => void
}

/** The ends of a span the editor has set. An end left out follows the seed. */
export interface SpanEdits {
  date?: string
  toDate?: string
}

/**
 * A span of days seeded from `start` and `span`, both of which it follows until
 * the editor takes an end over. Switching template is a fresh start for that,
 * and so is filing the memo into another planning: a planning says which days
 * the memo is about more strongly than a range the editor has already moved.
 */
export const useSeededSpan = ({ start, span, templateId, planningId }: {
  /** The day the span opens on until the editor moves it. */
  start: string
  /** How many days past the first the last one is seeded. */
  span: number
  /** The picked template's id, which is what a template change is noticed by. */
  templateId: string
  /** The planning the memo is filed into, if any. */
  planningId: string | undefined
}): SeededSpan => {
  const [edits, setEdits] = useSpanEdits<SpanEdits>({}, templateId, planningId)

  return useMemo(() => seededSpan(start, span, edits, setEdits), [start, span, edits, setEdits])
}

/**
 * Edits that start over, as `empty`, on a template or planning change. Reset
 * during render rather than in an effect, so no render sees the old ones.
 */
export const useSpanEdits = <T,>(
  empty: T,
  templateId: string,
  planningId: string | undefined
): [T, (update: (current: T) => T) => void] => {
  const [edits, setEdits] = useState<T>(empty)
  const [editedFor, setEditedFor] = useState({ templateId, planningId })

  const stale = editedFor.templateId !== templateId || editedFor.planningId !== planningId

  if (stale) {
    setEditedFor({ templateId, planningId })
    setEdits(empty)
  }

  return [stale ? empty : edits, setEdits]
}

/**
 * The span `start` and `span` seed, with the editor's `edits` on top. Worked
 * out during render, so the days are right from the first one.
 */
export function seededSpan(
  start: string,
  span: number,
  edits: SpanEdits,
  update: (next: (current: SpanEdits) => SpanEdits) => void
): SeededSpan {
  const date = edits.date ?? start
  const toDate = edits.toDate ?? shift(date, span)

  return {
    date,
    toDate,
    lastDate: toDate,
    validRange: toDate >= date,
    setDate: (value) => {
      // A cleared field, and a blank is not a day: the field is handed back to
      // the template's own suggestion rather than left holding nothing to parse.
      // A first day moved past a last day the editor set takes it along, so the
      // span cannot run backwards. An untouched last day follows anyway.
      update((current) => value
        ? {
            date: value,
            toDate: current.toDate && value > current.toDate ? value : current.toDate
          }
        : { ...current, date: undefined })
    },
    setToDate: (value) => {
      // Behind the first day is no span at all. The calendar does not offer one,
      // but its "today" button reaches around it, and today is behind the span
      // whenever the memo reaches ahead.
      update((current) => {
        const first = current.date ?? start

        return value
          ? { ...current, toDate: value < first ? first : value }
          : { ...current, toDate: undefined }
      })
    },
    shiftBy: (days) => {
      // Nothing to move, and nothing to take the span over from the memo's day
      // for either.
      if (!days) {
        return
      }

      // An untouched last day follows the first, so only a set one is moved.
      update((current) => ({
        date: shift(current.date ?? start, days),
        toDate: current.toDate ? shift(current.toDate, days) : undefined
      }))
    }
  }
}
